import { useState, useEffect, useCallback } from 'react';
import { getItem, setItem, removeItem } from '../utils/indexedDB';

/**
 * Custom hook to persist form state in localStorage or IndexedDB with race-condition-free key switching.
 * 
 * @param {string} key - Unique key for storage.
 * @param {any} initialState - Initial state if no data is found.
 * @param {object} options - Options for persistence (useIndexedDB, serializer, deserializer).
 * @returns {[any, Function, Function, boolean]} - Returns [state, setState, clearPersistence, isLoaded].
 */
const useFormPersistence = (key, initialState, options = {}) => {
  const {
    serializer = JSON.stringify,
    deserializer = JSON.parse,
    useIndexedDB = false
  } = options;

  const [prevKey, setPrevKey] = useState(key);
  const [isLoaded, setIsLoaded] = useState(!useIndexedDB);
  
  // Initialize state from localStorage or initial state
  const [state, setState] = useState(() => {
    if (!key || useIndexedDB) return initialState; // For async, return initial state first
    try {
      const saved = localStorage.getItem(key);
      return saved ? deserializer(saved) : initialState;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return initialState;
    }
  });

  // Async load for IndexedDB when key changes
  useEffect(() => {
    if (!key) return;
    
    if (useIndexedDB) {
      let isMounted = true;
      setIsLoaded(false);
      
      getItem(key).then(saved => {
        if (!isMounted) return;
        if (saved !== undefined && saved !== null) {
           try {
             setState(deserializer(saved));
           } catch (e) {
             console.error("Error parsing IndexedDB data", e);
           }
        }
        // If saved is empty, we purposefully DO NOT reset to initialState here.
        // This preserves any data the user typed before the key changed (e.g. before selecting a project),
        // and preserves any auto-filled data that triggered the key change.
        setIsLoaded(true);
      }).catch(err => {
        console.error("Error reading from IndexedDB:", err);
        if (isMounted) setIsLoaded(true);
      });
      
      return () => { isMounted = false; };
    }
  }, [key, useIndexedDB, deserializer]); 

  // Save to storage whenever state changes
  useEffect(() => {
    if (!key) return;

    if (useIndexedDB) {
      if (!isLoaded) return; // Wait until initial load is complete before saving!
      setItem(key, serializer(state)).catch(console.error);
    } else {
      try {
        localStorage.setItem(key, serializer(state));
      } catch (error) {
        if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
          console.warn("LocalStorage quota exceeded, draft may not be fully saved:", error);
        } else {
          console.error("Error saving to localStorage:", error);
        }
      }
    }
  }, [key, state, isLoaded, useIndexedDB, serializer]);

  // Function to clear the stored data
  const clearPersistence = useCallback(() => {
    if (!key) return;
    
    if (useIndexedDB) {
      removeItem(key).catch(console.error);
    } else {
      localStorage.removeItem(key);
    }
  }, [key, useIndexedDB]);

  // Adjust state synchronously during render if the key has changed
  let currentState = state;
  if (key !== prevKey) {
    setPrevKey(key);
    
    if (!useIndexedDB) {
      try {
        const saved = localStorage.getItem(key);
        if (saved) {
          const newValue = deserializer(saved);
          setState(newValue);
          currentState = newValue;
        }
        // Again, purposefully omitting a reset to initialState if empty,
        // to prevent wiping data when switching to a new/empty key.
      } catch (error) {
        console.error("Error reading from localStorage during key change:", error);
      }
    }
    // For IndexedDB, the useEffect will handle loading. We keep currentState as is to avoid UI flickering/wiping.
  }

  return [currentState, setState, clearPersistence, isLoaded];
};

export default useFormPersistence;

